import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

import { GLOBAL_SEARCH_MAX_LIMIT, GlobalSearchType, IGlobalSearchResult } from "@eagerdog/interfaces";

import Icon from "../Icon/Icon";
import { useGlobalSearch } from "./useGlobalSearch";

import styles from "./GlobalSearch.module.scss";

interface IProps {
  /** Mobile: render as a full-width row with a Cancel button. */
  expanded?: boolean;
  /** Mobile: called by the Cancel button and after a selection. */
  onClose?: () => void;
  className?: string;
}

type Tab = "all" | GlobalSearchType;

/** Display order of sections and filter tabs. */
const TYPE_ORDER: GlobalSearchType[] = ["event", "dog", "club", "course"];

/** Rows shown per section on the "All" tab; a type's own tab shows everything fetched. */
const ALL_TAB_PREVIEW = 3;

const TYPE_PLURAL: Record<GlobalSearchType, string> = {
  dog: "Dogs",
  club: "Clubs",
  event: "Events",
  course: "Courses",
};

const TYPE_SINGULAR: Record<GlobalSearchType, string> = {
  dog: "Dog",
  club: "Club",
  event: "Event",
  course: "Course",
};

const TYPE_ICON: Record<GlobalSearchType, string> = {
  dog: "paw",
  club: "home",
  event: "calendar",
  course: "school",
};

export const pathFor = (r: IGlobalSearchResult): string => {
  switch (r.type) {
    case "dog":
      return `/dogs/${r.id}`;
    case "club":
      return `/k/${r.id}`;
    case "event":
      return `/e/${r.handle}`;
    case "course":
      return `/c/${r.handle}`;
  }
};

const locationLine = (r: IGlobalSearchResult): string =>
  [r.city, r.state, r.country].filter((p) => p && p.trim()).join(", ");

const shortDate = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** Right-hand chip: start date for dated types, otherwise the type label. */
const chipFor = (r: IGlobalSearchResult): string =>
  (r.type === "event" || r.type === "course" ? shortDate(r.start_date) : undefined) || TYPE_SINGULAR[r.type];

/** Bold the matched prefix of the display name (whole-name or any word). */
const highlight = (name: string, q: string): React.ReactNode => {
  const needle = q.trim().toLowerCase();
  if (!needle) return name;
  const lower = name.toLowerCase();
  let start = lower.startsWith(needle) ? 0 : -1;
  if (start === -1) {
    const idx = lower.indexOf(` ${needle}`);
    if (idx !== -1) start = idx + 1;
  }
  if (start === -1) return name;
  const end = start + needle.length;
  return (
    <>
      {name.slice(0, start)}
      <strong>{name.slice(start, end)}</strong>
      {name.slice(end)}
    </>
  );
};

const GlobalSearch: React.FC<IProps> = ({ expanded = false, onClose, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const listId = useId();

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("all");

  // Fetch the max per type so a type's own tab can show more than the "All" preview.
  const { query, setQuery, status, response, results, activeIndex, setActiveIndex, moveActive, reset } =
    useGlobalSearch(GLOBAL_SEARCH_MAX_LIMIT);

  // Mobile expanded mode autofocuses.
  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  // Close and clear on route change.
  useEffect(() => {
    setOpen(false);
    setTab("all");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // A new query starts back on "All".
  useEffect(() => {
    setTab("all");
  }, [query]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const counts = response?.counts;
  const totalCount = counts ? TYPE_ORDER.reduce((n, t) => n + (counts[t] || 0), 0) : 0;

  // Sections in display order, trimmed per tab. `i` is the flat index into the
  // visible list so keyboard navigation and aria-activedescendant line up.
  const sections: Array<{ type: GlobalSearchType; items: Array<{ r: IGlobalSearchResult; i: number }>; total: number }> = [];
  let flat = 0;
  TYPE_ORDER.forEach((type) => {
    if (tab !== "all" && tab !== type) return;
    const ofType = results.filter((r) => r.type === type);
    const shown = tab === "all" ? ofType.slice(0, ALL_TAB_PREVIEW) : ofType;
    if (shown.length === 0) return;
    sections.push({
      type,
      total: counts?.[type] || ofType.length,
      items: shown.map((r) => ({ r, i: flat++ })),
    });
  });
  const visible = sections.flatMap((s) => s.items.map((x) => x.r));

  const close = () => {
    setOpen(false);
    setTab("all");
    reset();
  };

  const select = (r: IGlobalSearchResult, position: number) => {
    ReactGA.event("search_select", { type: r.type, id: r.id, q: query.trim(), position });
    close();
    onClose?.();
  };

  const changeTab = (next: Tab) => {
    setTab(next);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        moveActive(1, visible.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveActive(-1, visible.length);
        break;
      case "Enter": {
        if (visible.length === 0) return;
        e.preventDefault();
        const idx = activeIndex >= 0 ? activeIndex : 0;
        const r = visible[idx];
        select(r, idx);
        navigate(pathFor(r));
        break;
      }
      case "Escape":
        e.preventDefault();
        if (open) {
          setOpen(false);
        } else if (query) {
          reset();
        } else {
          onClose?.();
        }
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const showDropdown = open && status !== "idle";
  const optionId = (i: number) => `${listId}-opt-${i}`;

  const subtitle = (() => {
    const q = query.trim();
    switch (status) {
      case "short":
        return "Type at least 2 characters";
      case "loading":
        return "Searching…";
      case "empty":
        return `No matches for “${q}”`;
      case "error":
        return "Search is unavailable right now";
      case "results":
        return `${totalCount} result${totalCount === 1 ? "" : "s"} for “${q}”`;
      default:
        return "";
    }
  })();

  return (
    <div
      ref={rootRef}
      className={[styles.GlobalSearch, expanded ? styles.expanded : "", className || ""].filter(Boolean).join(" ")}
    >
      <div className={styles.pill}>
        <Icon type="search" className={styles.searchIcon} />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          placeholder="Search dogs, clubs, events…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            className={styles.clearBtn}
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              reset();
              inputRef.current?.focus();
            }}
          >
            <Icon type="close" className={styles.clearIcon} />
          </button>
        )}
      </div>

      {expanded && (
        <button type="button" className={styles.cancelBtn} onClick={() => { reset(); onClose?.(); }}>
          Cancel
        </button>
      )}

      {showDropdown && (
        <div className={styles.dropdown}>
          <div className={styles.panelHeader}>
            <div className={styles.panelTitles}>
              <p className={styles.panelTitle}>Search Eager Dog</p>
              <p className={styles.panelSubtitle}>{subtitle}</p>
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              aria-label="Close search"
              onMouseDown={(e) => e.preventDefault()}
              onClick={close}
            >
              <Icon type="close" className={styles.closeIcon} />
            </button>
          </div>

          {status === "results" && (
            <div className={styles.tabs} role="tablist" aria-label="Filter results by type">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "all"}
                className={[styles.tab, tab === "all" ? styles.tabActive : ""].filter(Boolean).join(" ")}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => changeTab("all")}
              >
                All
              </button>
              {TYPE_ORDER.map((type) => {
                const n = counts?.[type] || 0;
                return (
                  <button
                    key={type}
                    type="button"
                    role="tab"
                    aria-selected={tab === type}
                    disabled={n === 0}
                    className={[styles.tab, tab === type ? styles.tabActive : ""].filter(Boolean).join(" ")}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => changeTab(type)}
                  >
                    {TYPE_PLURAL[type]}
                  </button>
                );
              })}
            </div>
          )}

          <div className={styles.body} id={listId} role="listbox" aria-label="Search results">
            {status === "loading" && (
              <div className={styles.skeletons} aria-hidden="true">
                <span /><span /><span />
              </div>
            )}

            {status === "results" &&
              sections.map((s) => (
                <div key={s.type} className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span className={styles.sectionTitle}>{TYPE_PLURAL[s.type]}</span>
                    <span className={styles.sectionCount}>{s.total}</span>
                    {tab === "all" && s.total > s.items.length && (
                      <button
                        type="button"
                        className={styles.viewAll}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => changeTab(s.type)}
                      >
                        View all
                      </button>
                    )}
                  </div>

                  {s.items.map(({ r, i }) => {
                    const loc = locationLine(r);
                    return (
                      <Link
                        key={`${r.type}-${r.id}`}
                        id={optionId(i)}
                        role="option"
                        aria-selected={i === activeIndex}
                        to={pathFor(r)}
                        className={[styles.row, i === activeIndex ? styles.active : ""].filter(Boolean).join(" ")}
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => select(r, i)}
                      >
                        <span className={styles.tile}>
                          {r.image_url ? (
                            <img src={r.image_url} alt="" className={styles.tileImage} />
                          ) : (
                            <Icon type={TYPE_ICON[r.type]} className={styles.tileIcon} />
                          )}
                        </span>
                        <span className={styles.rowText}>
                          <span className={styles.rowTop}>
                            <span className={styles.name}>{highlight(r.name, query)}</span>
                            {r.is_mine && <span className={styles.mine}>Yours</span>}
                          </span>
                          {(r.secondary || loc) && (
                            <span className={styles.rowSub}>
                              {r.secondary}
                              {r.secondary && loc ? " · " : ""}
                              {loc}
                            </span>
                          )}
                        </span>
                        <span className={styles.chip}>{chipFor(r)}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
